import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiService } from '../services/api';
import ToastContainer from '../components/ToastContainer';
import { exportReportsToCSV } from '../utils/csvExport';


type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastItem {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
}

const Reports: React.FC = () => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [assignments, setAssignments] = useState<any[]>([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [limit] = useState<number>(10); // 10 rows per page
  
  // Filters and search
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>(''); // Local state for input (updates immediately)

  // Format time to IST (Indian Standard Time - UTC+5:30)
  const formatTimeToIST = (timeString: string | Date | null | undefined): string => {
    if (!timeString || timeString === "" || timeString === "-") return "";
    
    try {
      // If it's already in HH:MM format, assume it's IST and return as-is
      if (typeof timeString === 'string' && /^\d{2}:\d{2}(:\d{2})?$/.test(timeString)) {
        return timeString.substring(0, 5); // Return HH:MM format
      }
      
      // If it's an ISO string or date object, convert to IST
      const date = new Date(timeString);
      if (isNaN(date.getTime())) return "";
      
      // Use toLocaleString with Asia/Kolkata timezone for proper IST conversion
      const istTimeString = date.toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      
      return istTimeString;
    } catch (error) {
      console.error('Error formatting time to IST:', error);
      return "";
    }
  };

  // Format date to IST date format (DD/MM/YYYY)
  const formatDateToIST = (dateString: string | Date | null | undefined): string => {
    if (!dateString) return "";
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      
      // Use toLocaleString with Asia/Kolkata timezone for proper IST conversion
      const istDateString = date.toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      
      // Format as DD/MM/YYYY (en-IN gives DD/MM/YYYY format)
      return istDateString;
    } catch (error) {
      console.error('Error formatting date to IST:', error);
      return "";
    }
  };

  // Get day name in IST
  const getDayNameIST = (dateString: string | Date | null | undefined): string => {
    if (!dateString) return "";
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      
      // Use toLocaleString with Asia/Kolkata timezone
      const dayName = date.toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        weekday: 'long'
      });
      
      return dayName;
    } catch (error) {
      return "";
    }
  };

  // Calculate OTIF (On-Time In-Full)
  // Returns "Yes" if driver reaches within 30 minutes of scheduled time (before or up to 30 min after)
  // Returns "No" if driver reaches more than 30 minutes after scheduled time
  const calculateOTIF = (pickupTimeStr: string, actualReportingTimeStr: string): string => {
    if (!pickupTimeStr || pickupTimeStr === "" || !actualReportingTimeStr || actualReportingTimeStr === "") {
      return "";
    }

    try {
      // Parse times assuming they are in HH:MM format
      const parseTime = (timeStr: string): Date => {
        const [hours, minutes] = timeStr.split(':').map(Number);
        const d = new Date(); // Use current date to create time objects
        d.setHours(hours, minutes, 0, 0);
        return d;
      };

      const pickupTime = parseTime(pickupTimeStr);
      const actualReportingTime = parseTime(actualReportingTimeStr);

      if (isNaN(pickupTime.getTime()) || isNaN(actualReportingTime.getTime())) {
        return "";
      }

      // Calculate difference in minutes
      // Positive = actualReporting is after pickupTime (late)
      // Negative = actualReporting is before pickupTime (early)
      const diffMinutes = (actualReportingTime.getTime() - pickupTime.getTime()) / (1000 * 60);

      // If driver reaches within 30 minutes (before or up to 30 min after), it's "Yes"
      // If driver reaches more than 30 minutes after, it's "No"
      // Early arrival (negative diffMinutes) is always "Yes"
      return diffMinutes <= 30 ? "Yes" : "No";
    } catch (error) {
      console.error('Error calculating OTIF:', error);
      return "";
    }
  };

  const getLastCheckpointCompleteTime = (checkpoints: any[], tripStartDetails: any = null) => {
    // Priority 1: Use endTripTime from tripStartDetails if available (most reliable source)
    if (tripStartDetails?.endTripTime) {
      return tripStartDetails.endTripTime;
    }
    
    if (!Array.isArray(checkpoints) || checkpoints.length === 0) return '';
    
    // Priority 2: Try to find last checkpoint with completeLoadIn.completed === true and has time
    for (let i = checkpoints.length - 1; i >= 0; i--) {
      const cp = checkpoints[i];
      if (cp?.completeLoadIn?.completed && cp?.completeLoadIn?.time) return cp.completeLoadIn.time;
    }
    
    // Priority 3: Use last checkpoint's completeLoadIn.time even if completed flag is not set
    for (let i = checkpoints.length - 1; i >= 0; i--) {
      const cp = checkpoints[i];
      if (cp?.completeLoadIn?.time) return cp.completeLoadIn.time;
      if (cp?.startLoadOut?.time) return cp.startLoadOut.time;
    }
    return '';
  };

  const addToast = (message: string, type: ToastType = 'info') => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setToasts(prev => [...prev, { id, message, type }]);
  };
  const removeToast = (id: string) => setToasts(prev => prev.filter(t => t.id === id));

  const loadData = useCallback(async () => {
    // Don't show loading for very short searches (less than 2 characters) unless it's clearing the search
    const shouldShowLoading = searchQuery.length === 0 || searchQuery.length >= 2;
    
    if (shouldShowLoading) {
      setLoading(true);
    }
    
    try {
      const queryParams: Record<string, any> = {
        page: currentPage,
        limit: limit,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      };

      // Add filters
      if (statusFilter) {
        queryParams.status = statusFilter;
      }
      if (searchQuery.trim()) {
        queryParams.search = searchQuery.trim();
      }

      const response = await apiService.getVendorAssigneReport(queryParams);
      console.log('Reports page - API response:', response);

      // API service now returns { success: true, data: [...], pagination: {...} }
      if (response.success && Array.isArray(response.data)) {
        setAssignments(response.data);
        
        // Update pagination info from backend
        if (response.pagination) {
          setCurrentPage(response.pagination.currentPage || currentPage);
          setTotalPages(response.pagination.totalPages || 0);
          setTotalCount(response.pagination.totalCount || 0);
        }
        
        console.log(`Loaded ${response.data.length} assignments (Page ${currentPage}/${response.pagination?.totalPages || 0})`);
      } else {
        console.warn('Invalid response structure:', response);
        setAssignments([]);
        if (response.error) {
          addToast(response.error, 'error');
        }
      }
    } catch (err) {
      setAssignments([]);
      addToast('Network error: Failed to load vendor assignments', 'error');
    } finally {
      // Only hide loading if we showed it
      const shouldShowLoading = searchQuery.length === 0 || searchQuery.length >= 2;
      if (shouldShowLoading) {
        setLoading(false);
      }
    }
  }, [currentPage, limit, statusFilter, searchQuery]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      const trimmedInput = searchInput.trim();
      if (trimmedInput.length === 0 || trimmedInput.length >= 2) {
        if (trimmedInput !== searchQuery.trim()) {
          setSearchQuery(trimmedInput);
        }
      }
    }, 1000);

    return () => clearTimeout(debounceTimer);
  }, [searchInput, searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, searchQuery]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle page change
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleExport = () => {
    if (!flattenedRows.length) {
      addToast('No data to export', 'warning');
      return;
    }
    const rows = flattenedRows.map(r => ({
      Date: r.Date,
      Day: r.Day,
      TripId: r.TripId,
      VehicleNo: r.VehicleNo,
      VehicleType: r.VehicleType,
      DriverName: r.DriverName,
      ClientCity: r.ClientCity,
      VehicleCategory: r.VehicleCategory,
      ReportingTime: r.ReportingTime,
      ActualReporting: r.ActualReporting,
      EndTime: r.EndTime,
      OTIF: r.OTIF,
      TotalKms: r.TotalKms,
      TotalHours: r.TotalHours,
      DropPoints: r.DropPoints,
      ContractedHours: r.ContractedHours,
      CostPerDay: r.CostPerDay,
      CostPerDrop: r.CostPerDrop,
      CostPerKm: r.CostPerKm,
    }));
    exportReportsToCSV(rows as any[], 'vendor-reports');
    addToast('Report exported successfully', 'success');
  };


  const flattenedRows = useMemo(() => {
    const rows: Array<Record<string, any>> = [];
    const source = assignments;
    
    source.forEach((a: any) => {
      const createdAt = a.createdAt || a.updatedAt || a.startDate || '';
      const reportingTime = formatTimeToIST(a.pickupTime);
      const actualReporting = formatTimeToIST(a.actualReporting || a.timerStartTime || a.pickupTime);
      const checkpoints = Array.isArray(a.checkPoint) ? a.checkPoint : [];
      // Priority 1: Use totalKms from backend computation (if available)
      // Priority 2: Use totalDistanceKm from assignment model (if available)
      // Priority 3: Calculate from checkpoints if neither is available
      const totalKms = a.totalKms 
        ? parseFloat(a.totalKms) || 0
        : (a.totalDistanceKm 
          ? parseFloat(a.totalDistanceKm) || 0
          : checkpoints.reduce((acc: number, cp: any) => acc + (parseFloat(cp?.checkpointKm || '0') || 0), 0));
      const dropPoints = checkpoints.length;
      const endTimeStr = getLastCheckpointCompleteTime(checkpoints, a.tripStartDetails);
      const endTime = formatTimeToIST(endTimeStr);
      const vehicleNum = a.vehicleId?.vehicleNumber || '';
      const vehicleType = a.vehicleId?.vehicleType || '';
      const clientCity = a?.companyName?.location || a?.endPointName || a?.startPointName || '';
      const CostPerDay = a?.costWithoutTax || '';
      const CostPerDrop = (a?.costWithoutTax / dropPoints).toFixed(2) || '';
      const CostPerKm = (a?.costWithoutTax / totalKms).toFixed(2) || '';
      const formattedDate = formatDateToIST(createdAt);
      const dayName = getDayNameIST(createdAt);
      // Calculate OTIF: Yes if within 30 minutes, No if more than 30 minutes late
      const otifValue = calculateOTIF(reportingTime, actualReporting);
      rows.push({
        Date: formattedDate,
        Day: dayName,
        TripId: a.tripId || '',
        VehicleNo: vehicleNum,
        VehicleType: vehicleType,
        DriverName: a?.driverId?.name || '',
        ClientCity: clientCity,
        VehicleCategory: a?.tempRequired ? 'Reefer' : 'Standard',
        ReportingTime: reportingTime,
        ActualReporting: actualReporting, 
        EndTime: endTime === '' ? '-' : endTime, 
        OTIF: otifValue, 
        TotalKms: totalKms ? totalKms.toFixed(2) : '-',
        TotalHours: a.durationHours ? a.durationHours.toFixed(2) : '',
        DropPoints: dropPoints || '',
        CostPerDay: CostPerDay === 'Infinity' ? '-' : CostPerDay,
        CostPerDrop: CostPerDrop === 'Infinity' ? '-' : CostPerDrop,
        CostPerKm: CostPerKm === 'Infinity' ? '-' : CostPerKm,
        __status: a.deliveryStatus || '',
        __vehicleType: vehicleType,
      });
    });
    
    return rows;
  }, [assignments]);

  const dynamicSummary = useMemo(() => {
    const totalVehicles = new Set(flattenedRows.map((a: any) => a.VehicleNo)).size;
    const pageAssignments = flattenedRows.length;
    const completedCount = flattenedRows.filter((a: any) => (
      a.__status === 'delivered' || a.__status === 'completedByAdmin'
    )).length;
    const completionRate = pageAssignments > 0 ? ((completedCount / pageAssignments) * 100).toFixed(2) : '0.00';
    const totalDistance = flattenedRows.reduce((acc: number, r: any) => acc + (parseFloat(r.TotalKms || '0') || 0), 0).toFixed(2);
    return { totalVehicles, totalAssignments: totalCount || pageAssignments, totalDistance, completionRate };
  }, [flattenedRows, totalCount]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6">
        <div className="mb-4">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Reports</h1>
          <p className="text-gray-600 mt-1">Delivery performance and vehicle assignments</p>
        </div>


        <div className="bg-white rounded-lg shadow border mb-4">
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <div className="text-sm text-gray-500">Latest report</div>
              <button onClick={handleExport} className="inline-flex items-center px-4 py-2 bg-blue-100 text-jd-blue rounded-lg hover:bg-blue-200 transition-colors">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export Report
              </button>
            </div>

            {/* Filters and Search */}
            {!loading && (
              <div className="bg-white p-4 rounded-lg shadow border mb-4">
                <div className="flex flex-wrap items-end gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                    <input 
                      value={searchInput} 
                      onChange={e => setSearchInput(e.target.value)} 
                      placeholder="Search by trip ID, vehicle, driver, city..." 
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">All</option>
                      <option value="upcoming">Upcoming</option>
                      <option value="ongoing">Ongoing</option>
                      <option value="delivered">Delivered</option>
                      <option value="completedByAdmin">Completed By Admin</option>
                    </select>
                  </div>
                  <button 
                    onClick={() => { 
                      setStatusFilter(''); 
                      setSearchInput('');
                      setSearchQuery('');
                    }} 
                    className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                  >
                    Clear All
                  </button>
                </div>
              </div>
            )}

            {loading && (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                <span className="ml-2 text-gray-600">Loading delivery report...</span>
              </div>
            )}

            {!loading && !assignments.length && (
              <div className="text-center py-8">
                <p className="text-gray-500">No report data available</p>
                <button onClick={loadData} className="mt-4 px-4 py-2 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors">Retry Loading</button>
              </div>
            )}

            {!loading && (
              <div className="space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <div className="bg-white p-4 rounded-lg shadow border">
                    <h4 className="text-sm font-medium text-gray-500">Total Vehicles</h4>
                    <p className="text-2xl font-bold text-blue-600">{dynamicSummary.totalVehicles}</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow border">
                    <h4 className="text-sm font-medium text-gray-500">Total Assignments</h4>
                    <p className="text-2xl font-bold text-green-600">{dynamicSummary.totalAssignments}</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow border">
                    <h4 className="text-sm font-medium text-gray-500">Total Distance</h4>
                    <p className="text-2xl font-bold text-purple-600">{dynamicSummary.totalDistance} km</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow border">
                    <h4 className="text-sm font-medium text-gray-500">Completion Rate</h4>
                    <p className="text-2xl font-bold text-orange-600">{dynamicSummary.completionRate}%</p>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow border">
                  <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Delivery Report</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Showing {flattenedRows.length} of {totalCount} records (Page {currentPage} of {totalPages})
                      </p>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>

                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trip Id</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Day</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">VehicleNo</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vehicle Type</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Driver Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client City</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vehicle Category</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reporting Time</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actual Reporting</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Time</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">OTIF</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Kms</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Hours</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Drop Points</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost Per Day</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost Per Drop</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost Per Km</th>

                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {flattenedRows.length > 0 ? (
                          flattenedRows.map((r, i) => (
                            <tr key={i} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.TripId}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.Day}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.Date}</td>


                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.VehicleNo}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.VehicleType}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.DriverName}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.ClientCity}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.VehicleCategory}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.ReportingTime}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.ActualReporting}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.EndTime}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.OTIF}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.TotalKms}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.TotalHours}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.DropPoints}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.CostPerDay}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.CostPerDrop}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.CostPerKm}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={19} className="px-6 py-4 text-center text-gray-500">No data available</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-700">
                          Page {currentPage} of {totalPages}
                        </span>
                        <span className="text-sm text-gray-500">
                          ({totalCount} total records)
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handlePageChange(1)}
                          disabled={currentPage === 1}
                          className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          First
                        </button>
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        <span className="px-3 py-1 text-sm text-gray-700">
                          {currentPage}
                        </span>
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                        <button
                          onClick={() => handlePageChange(totalPages)}
                          disabled={currentPage === totalPages}
                          className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Last
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
      </div>
    </div>
  );
};

export default Reports;


